import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setCurrency } from 'common/actions/format';
import * as messages from 'common/constants/messages/validations';
import { Units } from 'common/types/format';
import { MILLIONS, THOUSANDS } from 'components/FeaturedSpreadsheet/constants';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useResponse } from 'services/hooks';
import { getExchangeRate } from 'utilities';
import CurrencyFormatter from './CurrencyFormatter';
import { CustomCurrency, GridFormatterProps } from './GridFormatter.props';
import { StyledRoot } from './GridFormatter.styles';
import UnitsFormatter from './UnitsFormatter';

const NUMBER_UNITS = ['', THOUSANDS, MILLIONS];

const GridFormatter = ({ currencyFormatter, unitsFormatter }: GridFormatterProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { errorNotification } = useResponse();
  const { marketDate } = useContext(ValuationContext);
  const {
    format,
    formatDispatch,
    setCurrency: updateCurrencyState,
    currency: strCurrency,
  } = useContext(FeaturedSpreadsheetContext);

  const tableFormat = format && formatDispatch;

  const currency: CustomCurrency = useMemo(() => {
    if (tableFormat) {
      return format.currency;
    }

    const [code, symbol, units = ''] = strCurrency.split(' ');
    return { code, symbol, units };
  }, [format, strCurrency, tableFormat]);

  const onUnitsChanged = () => {
    const currentUnitsIndex = NUMBER_UNITS.indexOf(currency.units ?? '');
    const nextNumberFormatIndex = (currentUnitsIndex + 1) % NUMBER_UNITS.length;
    const units = NUMBER_UNITS[nextNumberFormatIndex] as Units;

    if (tableFormat) {
      formatDispatch(setCurrency({ units }));
    } else {
      const { code, symbol } = currency;
      updateCurrencyState(`${code} ${symbol} ${units}`);
    }
  };

  const onCurrencyChanged = useCallback(
    async (newCurrency: CustomCurrency) => {
      setIsLoading(true);
      if (tableFormat) {
        const currencyDate = marketDate && marketDate !== '' ? marketDate : undefined;
        try {
          let additionalCurrencies;
          const exchangeRate = await getExchangeRate(
            currency.sourceCurrency as string,
            newCurrency.code as string,
            currency.measurementDateId as string,
            currencyDate
          );

          if (currency.additionalCurrencies) {
            additionalCurrencies = await Promise.all(
              currency.additionalCurrencies.map(async additionalCurrency => {
                const additionalExchangeRate = await getExchangeRate(
                  additionalCurrency.sourceCurrency as string,
                  newCurrency.code as string,
                  currency.measurementDateId as string,
                  marketDate || undefined
                );
                return {
                  ...additionalCurrency,
                  exchangeRate: Number(additionalExchangeRate),
                };
              })
            );
          }
          formatDispatch(
            setCurrency({ ...newCurrency, exchangeRate: Number(exchangeRate), marketDate, additionalCurrencies })
          );
        } catch (error) {
          errorNotification(messages.GET_EXCHANGE_RATE_ERROR);
        }
      } else {
        const { units } = currency;
        const { code, symbol } = newCurrency;
        updateCurrencyState(`${code} ${symbol} ${units}`);
      }
      setIsLoading(false);
    },
    [currency, formatDispatch, tableFormat, updateCurrencyState, errorNotification, marketDate]
  );

  useEffect(() => {
    if (format?.currency?.additionalCurrencies && !format.currency.exchangeRate) {
      onCurrencyChanged({ code: format.currency.sourceCurrency });
    }
  }, [format, onCurrencyChanged]);

  useEffect(() => {
    if (format?.currency && (format.currency.code !== currency.code || format.currency.marketDate !== marketDate)) {
      onCurrencyChanged(format.currency);
    }
  }, [format, onCurrencyChanged, currency.code, marketDate]);

  return (
    <StyledRoot>
      {currencyFormatter && (
        <CurrencyFormatter onCurrencyChanged={onCurrencyChanged} currency={currency} isLoading={isLoading} />
      )}
      {unitsFormatter && <UnitsFormatter currency={currency} onUnitsChanged={onUnitsChanged} isLoading={isLoading} />}
    </StyledRoot>
  );
};

export default GridFormatter;
